import { ContentfulImage, ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import { renameKeyFromTitleToText } from '@utils/objects.utils';
import { ButtonProps } from '@stadionhq-v3/base-ui';
import { ArrowRight, ArrowRightLight } from '@components/icons';
import {
  FixturesSummaryBlockProps,
  FixtureSummaryBlockCardWrapper,
  StandingsTableRequestProps,
} from '@stadionhq-v3/football-ui';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import templateConfig from '@templateConfig';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { Asset, Broadcaster, Link } from '@graphql/index';
import { Locale } from '@core/constants/locales.constants';
import { isRtlLocale } from '@utils/locales.utils/locales.utils';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { isLink } from '@core/types/typeGuards';
import {
  FixtureSummaryBlockWithTeamData,
  TeamWithData,
} from '@contentful/model/pages/common/page-subqueries';
import buttonStyles from '@components/v3/atoms/button/button.module.scss';
import classnames from 'classnames';
import { FixturesSummaryBlockMatchCard } from '@components/organisms/fixtures-summary-block-match-card';
import { HOME_AWAY_MATCH, OptaMatchStatus } from '@stadionhq-v3/core';
import { ERROR_MESSAGES } from '@core/constants/errorMessages';
import BroadcastersWrapper from '@components/organisms/broadcasters-wrapper/broadcastersWrapper.component';
import { linkTransform, assetTransform } from '../atoms';
import fixtureSummaryBlockLeagueTableTransform from './fixtureSummaryBlockLeagueTable.transform';
import broadcastersWrapperTransform from './broadcastersWrapper.transform';

export type FixtureSummaryBlockTranslations = {
  viewMatchCentre: Translations['fixtures']['action']['viewMatchCentre'];
  viewMatchReport: Translations['fixtures']['action']['viewMatchReport'];
  homeMatch: Translations['fixtures']['label']['homeMatch'];
  awayMatch: Translations['fixtures']['label']['awayMatch'];
  sponsoredBy: Translations['common']['label']['sponsoredBy'];
  lastMatch: Translations['fixturesSummaryBlock']['label']['lastMatch'];
  nextMatch: Translations['fixturesSummaryBlock']['label']['nextMatch'];
  upcomingMatch: Translations['fixturesSummaryBlock']['label']['upcomingMatch'];
  leagueTable: Translations['fixturesSummaryBlock']['label']['leagueTable'];
  live: Translations['fixtures']['status']['live'];
  cancelled: Translations['fixtures']['status']['cancelled'];
  abandoned: Translations['fixtures']['status']['abandoned'];
  postponed: Translations['fixtures']['status']['postponed'];
};

const generateCtaLinks = (
  match:
    | TeamWithData['lastMatch']
    | TeamWithData['nextMatch']
    | TeamWithData['upcomingMatch'],
  dictionary: FixtureSummaryBlockTranslations,
  locale: Locale,
  siteSettingsTicketingLink?: CleanContentful<Link>,
  siteSettingsHospitalityLink?: CleanContentful<Link>,
  type?: 'lastMatch' | 'nextMatch' | 'upcomingMatch'
): ButtonProps[] =>
  [
    match?.slug &&
      type === 'lastMatch' && {
        ...renameKeyFromTitleToText(
          linkTransform(
            {
              url: generateFullPathname({
                locale,
                route: templateConfig.navigation.routes.MATCH_CENTRE,
                params: {
                  team: match?.team?.slug,
                  fixture: match?.slug,
                },
              }),
              title: dictionary.viewMatchReport,
            },
            locale,
            <ArrowRightLight />
          )
        ),
        classNames: [
          classnames([buttonStyles.button, buttonStyles.buttonTokens]),
        ],
      },
    Boolean(
      isLink(match?.ticketingLink) || isLink(siteSettingsTicketingLink)
    ) &&
      type !== 'lastMatch' && {
        ...renameKeyFromTitleToText(
          linkTransform(
            match?.ticketingLink ||
              (siteSettingsTicketingLink as CleanContentful<Link>),
            locale,
            <ArrowRightLight />
          )
        ),
        classNames: [
          classnames([buttonStyles.button, buttonStyles.buttonTokens]),
        ],
      },
    match?.slug && {
      ...renameKeyFromTitleToText(
        linkTransform(
          {
            url: generateFullPathname({
              locale,
              route: templateConfig.navigation.routes.MATCH_CENTRE,
              params: {
                team: match?.team?.slug,
                fixture: match?.slug,
              },
            }),
            title: dictionary.viewMatchCentre,
          },
          locale,
          <ArrowRightLight />
        )
      ),
      classNames: [
        classnames([buttonStyles.button, buttonStyles.buttonTokens]),
      ],
    },
    ...(match?.additionalLinks?.filter(isLink).map((link) => ({
      ...(renameKeyFromTitleToText(
        linkTransform(link, locale, <ArrowRightLight />)
      ) as ButtonProps),
      classNames: [
        classnames([buttonStyles.button, buttonStyles.buttonTokens]),
      ],
    })) ?? []),
  ]
    .filter(Boolean)
    .slice(0, 1)
    .map((link, index) => ({
      ...link,
      variant: index === 0 ? 'primary' : 'secondary',
    })) as ButtonProps[];

const transformMatchData = (
  match:
    | TeamWithData['lastMatch']
    | TeamWithData['nextMatch']
    | TeamWithData['upcomingMatch'],
  dictionary: FixtureSummaryBlockTranslations,
  locale: Locale,
  siteSettingsTicketingLink?: CleanContentful<Link>,
  siteSettingsHospitalityLink?: CleanContentful<Link>,
  fallbackClubCrest?: CleanContentful<Asset>,
  type?: 'lastMatch' | 'nextMatch' | 'upcomingMatch'
) => ({
  venue: `${match?.venue ?? ''}`,
  locale,
  homeClub: {
    name: match?.homeClub?.name ?? '',
    crestImage: match?.homeClub?.clubCrest ? (
      <ContentfulLogo {...match.homeClub?.clubCrest} width={92} height={92} />
    ) : fallbackClubCrest ? (
      <ContentfulLogo
        {...assetTransform(fallbackClubCrest)}
        width={92}
        height={92}
      />
    ) : undefined,
    score: match?.matchData?.header?.home?.score,
    isClientTeam:
      match?.homeClub?.dataProviderId === match?.team?.dataProviderId,
  },
  awayClub: {
    name: match?.awayClub?.name ?? '',
    crestImage: match?.awayClub?.clubCrest ? (
      <ContentfulLogo {...match.awayClub?.clubCrest} width={92} height={92} />
    ) : fallbackClubCrest ? (
      <ContentfulLogo
        {...assetTransform(fallbackClubCrest)}
        width={92}
        height={92}
      />
    ) : undefined,
    score: match?.matchData?.header?.away?.score,
    isClientTeam:
      match?.awayClub?.dataProviderId === match?.team?.dataProviderId,
  },
  kickoffTime: match?.kickOffUtc,
  date: match?.kickOffUtc,
  homeAway:
    match?.team?.dataProviderId === match.homeClub?.dataProviderId
      ? HOME_AWAY_MATCH.HOME
      : HOME_AWAY_MATCH.AWAY,
  competitionName: match.competition?.name,
  matchPeriod: match.matchPeriod as OptaMatchStatus,
  ctaLinks: generateCtaLinks(
    match,
    dictionary,
    locale,
    siteSettingsTicketingLink,
    siteSettingsHospitalityLink,
    type
  ),
  labels: dictionary,
  broadcasters: (
    <BroadcastersWrapper
      {...broadcastersWrapperTransform(
        locale,
        match?.dataProviderId,
        match?.broadcasters as Broadcaster[]
      )}
    />
  ),
});

const fixtureSummaryBlockTransform = (
  block: FixtureSummaryBlockWithTeamData,
  dictionary: FixtureSummaryBlockTranslations,
  locale: Locale,
  sportsApiLabels: StandingsTableRequestProps['dictionary'],
  currentSeason?: string,
  siteSettingsHospitalityLink?: CleanContentful<Link>,
  siteSettingsTicketingLink?: CleanContentful<Link>,
  fallbackClubCrest?: CleanContentful<Asset>
): FixturesSummaryBlockProps => {
  const filteredItems = block.teams?.filter((item: TeamWithData) =>
    Boolean(item.lastMatch || item.nextMatch || item.upcomingMatch)
  );

  if (!process.env.NEXT_PUBLIC_SPORTS_DATA_API_BASE_URL) {
    throw new Error(ERROR_MESSAGES.MISSING_ENV_VAR('Sports Data API'));
  }

  return {
    backgroundImage: block.image ? (
      <ContentfulImage {...block.image} />
    ) : undefined,
    sponsor: block.sponsor
      ? {
          title: dictionary.sponsoredBy,
          image: <ContentfulImage {...assetTransform(block.sponsor)} />,
          onDark: true,
        }
      : undefined,
    ctaLink: block.cta
      ? {
          ...(renameKeyFromTitleToText(
            linkTransform(block.cta, locale, <ArrowRight />)
          ) as ButtonProps),
          onDark: true,
          classNames: [classnames([buttonStyles.button])],
        }
      : undefined,
    tabItems:
      filteredItems?.length > 1
        ? filteredItems?.map((team: TeamWithData) => ({
            id: team.slug ?? '',
            text: team.name ?? '',
            onDark: true,
          }))
        : undefined,
    groupedDataByTeams: filteredItems?.map((team: TeamWithData) => ({
      id: team.slug ?? '',
      lastMatch: team.lastMatch && (
        <FixtureSummaryBlockCardWrapper cardSubTitle={dictionary.lastMatch}>
          <FixturesSummaryBlockMatchCard
            {...transformMatchData(
              {
                ...team.lastMatch,
              },
              dictionary,
              locale,
              siteSettingsTicketingLink,
              siteSettingsHospitalityLink,
              fallbackClubCrest,
              'lastMatch'
            )}
          />
        </FixtureSummaryBlockCardWrapper>
      ),
      nextMatch: team.nextMatch && (
        <FixtureSummaryBlockCardWrapper cardSubTitle={dictionary.nextMatch}>
          <FixturesSummaryBlockMatchCard
            {...transformMatchData(
              {
                ...team.nextMatch,
              },
              dictionary,
              locale,
              siteSettingsTicketingLink,
              siteSettingsHospitalityLink,
              fallbackClubCrest,
              'nextMatch'
            )}
          />
        </FixtureSummaryBlockCardWrapper>
      ),
      upcomingMatch: team.upcomingMatch && !team.primaryLeagueCompetition && (
        <FixtureSummaryBlockCardWrapper cardSubTitle={dictionary.upcomingMatch}>
          <FixturesSummaryBlockMatchCard
            {...transformMatchData(
              {
                ...team.upcomingMatch,
              },
              dictionary,
              locale,
              siteSettingsTicketingLink,
              siteSettingsHospitalityLink,
              fallbackClubCrest,
              'upcomingMatch'
            )}
          />
        </FixtureSummaryBlockCardWrapper>
      ),
      leagueTableComplex:
        team.primaryLeagueCompetition &&
        team.primaryLeagueCompetition?.hasLeagueTable &&
        fixtureSummaryBlockLeagueTableTransform(
          { ...team },
          dictionary,
          locale,
          sportsApiLabels,
          currentSeason,
          fallbackClubCrest
        ),
    })),
    carouselOptions: {
      direction: isRtlLocale(locale) ? 'rtl' : 'ltr',
    },
  };
};

export default fixtureSummaryBlockTransform;
